import {
    Directive,
    ElementRef,
    forwardRef,
    Input,
    OnChanges,
    OnDestroy,
    SimpleChanges,
} from '@angular/core';
import { AbstractControl, NG_VALIDATORS, Validator } from '@angular/forms';
import { Subscription } from 'rxjs';

import { ValidationIndicatorDirective } from './common.form';

export const EMAIL_REGEXP = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

@Directive({
    selector: '[ngModel][requiredWhenValidator]',
    providers: [
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => RequiredWhenValidatorDirective),
            multi: true,
        },
    ],
})
export class RequiredWhenValidatorDirective
    implements OnChanges, OnDestroy, Validator
{
    @Input()
    requiredWhenValidator: string;

    @Input()
    operator: 'or' | 'not';

    @Input()
    markAsRequired: boolean;

    private _controls: AbstractControl[];
    private _subs: Subscription[];

    constructor(private readonly _el: ElementRef) {
        this.operator = 'or';
        this._controls = [];
        this._subs = [];
    }

    ngOnChanges(changes: SimpleChanges): void {
        this._reset();
    }

    ngOnDestroy(): void {
        this._reset();
    }

    validate(c: AbstractControl): { [p: string]: any } {
        this._reset();
        const fields = this.requiredWhenValidator.split('|');
        fields.forEach((field) => {
            const control = c.root.get(field);
            if (control) {
                this._controls.push(control);
                this._subs.push(
                    control.statusChanges.subscribe((status) => {
                        const wasValid = c.valid;
                        c.markAsDirty();
                        c.updateValueAndValidity({ emitEvent: false });
                        if (
                            (wasValid && !c.valid && !control.valid) ||
                            (!wasValid && c.valid && control.valid) ||
                            (wasValid && !c.valid && control.valid)
                        ) {
                            ValidationIndicatorDirective.clear(
                                this._el.nativeElement,
                            );
                        }
                    }),
                );
            }
        });
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        const controls = this._controls.filter(
            (control) =>
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                control && control.value != null && control.value.length !== 0,
        );
        const othersNotValid =
            this.operator === 'or'
                ? controls.length !== 0
                : controls.length === 0;
        const notValid =
            othersNotValid && (c.value == null || c.value.length === 0);
        return notValid ? { requiredWhen: true } : null;
    }

    private _reset(): void {
        this._subs.forEach((value) => value.unsubscribe());
        this._subs = [];
        this._controls = [];
    }
}

@Directive({
    selector:
        'input[type=text][textEqualValidator], input[type=password][textEqualValidator]',
    providers: [
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => TextEqualValidatorDirective),
            multi: true,
        },
    ],
})
export class TextEqualValidatorDirective implements Validator {
    @Input()
    textEqualValidator: string;

    validate(c: AbstractControl): { [p: string]: any } {
        const control = c.root.get(this.textEqualValidator);
        return control &&
            (control.value == null ||
                control.value.length === 0 ||
                control.value === c.value)
            ? null
            : { textEqual: true };
    }
}
